import axios from 'axios';
import queryString from 'query-string';
import { BannerLocationType, BannerTypeType } from './types';
import { PickDto, TrackingEventDto, VotingEventDto } from '../../types/generated/frontend-dwh';
import { Subject } from 'rxjs';
import { bufferTime, filter } from 'rxjs/operators';
import { getGeneralTrackingData, getTrackingClickData, getTackingPageViewData } from './getTrackingData';
import { nuiCtx } from '../../common/initialization';

const BUFFER_TIME = 1500;

const trackEvent = eventData => {
    const generalData = getGeneralTrackingData();
    const event: TrackingEventDto = Object.assign(generalData, eventData) as TrackingEventDto;
    trackingEvent$.next(event);
    return new Promise<void>(r => setTimeout(r, BUFFER_TIME + 100));
};

const trackingEvent$ = new Subject<TrackingEventDto>();
trackingEvent$
    .pipe(
        bufferTime(BUFFER_TIME),
        filter(array => array.length > 0)
    )
    .forEach(callDwhTrackEvents);

function callDwhTrackEvents(trackEvents: TrackingEventDto[]) {
    return axios.post<never, TrackingEventDto>(nuiCtx.properties['fe-dwh.endpoint.url'] + '/tle', trackEvents);
}

export const trackDwhPageViewEvent = (additionalPageViewData?: string) => {
    const pageViewData = { pageView: getTackingPageViewData(additionalPageViewData) };
    return trackEvent(pageViewData);
};

export const trackDwhClickEvent = (event: MouseEvent) => {
    const elementClickData = { elementClick: getTrackingClickData(event) };
    return trackEvent(elementClickData);
};

export const trackDwhBannerEvent = (
    eventName: 'show' | 'click',
    bannerLocation?: BannerLocationType,
    bannerType?: BannerTypeType,
    bannerId?: string,
    bannerIndex?: number,
    bannerUrl?: string
) => {
    // temporarily disable show event (we have too many events in kafka)
    if (eventName === 'show') {
        return;
    }
    const bannerData = {
        banner: { eventName, bannerLocation, bannerType, bannerId, bannerIndex, bannerUrl }
    };
    return trackEvent(bannerData);
};

export const trackSignUpEvent = (eventName, signUpPhase, prospectId) => {
    const { source, sportId, tournamentId, matchId } = queryString.parseUrl(window.location.href).query;
    const openIdentifier = { source: source || 'unknown', sportId, tournamentId, matchId };
    return trackEvent({ ...getGeneralTrackingData(), signUp: { eventName, signUpPhase, prospectId, openIdentifier } });
};

export const registrationQueryParams = (source: string, sportId?: string, tournamentId?: string, matchId?: string) => ({
    source,
    sportId,
    tournamentId,
    matchId
});

export const trackDwhVideoEvent = (
    eventName: string,
    videoUrl: string,
    videoProvider: string,
    videoTime: number,
    matchId?: string,
    sportId?: string,
    tournamentId?: string,
    archiveId?: string
) => {
    const videoData = {
        video: {
            eventName,
            matchId,
            tournamentId,
            sportId,
            archiveId,
            videoUrl,
            videoProvider,
            videoTime: videoTime?.toString()
        }
    };
    return trackEvent(videoData);
};

export const trackDwhBetslipEventThirdParty = (
    eventName: 'add-third-party-pick-success' | 'add-third-party-pick-error',
    betslipId: string,
    pick?: PickDto,
    betslipErrorReason?: string
) => {
    const betsplipData = {
        betslip: {
            eventName,
            betslipId,
            ticketId: null,
            stake: null,
            gameType: null,
            pick,
            betslipErrorReason
        }
    };
    return trackEvent(betsplipData);
};

export const trackDwhBetslipEventSubmit = (
    betslipId: string,
    ticketId?: string,
    stake?: number,
    gameType?: 'Prematch' | 'Live' | 'Hybrid' | 'Virtual'
) => {
    const betslipData = {
        betslip: {
            eventName: 'submit',
            betslipId,
            ticketId,
            stake,
            gameType,
            pick: null,
            betslipErrorReason: null
        }
    };
    return trackEvent(betslipData);
};

export const trackDwhCashinEvent = (
    eventName: 'manual_cashin' | 'set_automatic_cashin' | 'reset_automatic_cashin',
    ticketId: string,
    gameType: 'Prematch' | 'Live' | 'Hybrid' | 'Virtual',
    position:
        | 'TICKET_DETAIL'
        | 'TICKET_LIST_SIDEBAR'
        | 'PREMATCH_CASHIN_TICKETS'
        | 'PREMATCH_OVERVIEW'
        | 'PREMATCH_OVERVIEW_ON_TICKETS'
        | 'PREMATCH_DETAIL'
        | 'PREMATCH_DETAIL_MY_TICKETS'
        | 'LIVE_OVERVIEW'
        | 'LIVE_OVERVIEW_TICKET_LIST'
        | 'LIVE_DETAIL'
        | 'LIVE_DETAIL_MATCH_LIST'
        | 'LIVE_DETAIL_MY_TICKETS'
        | 'BETBOOK_TICKET_DETAIL'
) => {
    return trackEvent({
        cashin: { eventName, ticketId, gameType, position }
    });
};

export const trackDwhVotingEvent = (votingEvent: VotingEventDto) => {
    return trackEvent({
        voting: votingEvent
    });
};

export const trackDwhVotingClickEvent = (e, url) => {
    const votingClickEvent = getTrackingClickData(e);
    votingClickEvent.elementUrl = url;
    return trackEvent({ elementClick: votingClickEvent });
};
